import { CartAddEvent } from '@theme/events';

// /cart/add.js's own JSON response never carries item_count (unlike /cart/change.js, it
// returns the added item(s), not the full cart), so the header cart badge cannot be told
// the new total unless we pull it out of the section HTML we requested via `sections`.
// Mirrors component-cart-items.js's [ref="cartItemCount"] extraction so the two paths
// agree on where the number lives. Duplicated (not shared) with the identical helper in
// powerlete-cart-upsell.js: these are separate theme-asset modules, matching this file's
// existing pattern of self-contained modules with no cross-file imports beyond @theme/*.
function extractCartItemCount(sectionsHtml, sectionIds) {
  for (const id of sectionIds) {
    const html = sectionsHtml?.[id];
    if (!html) continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = doc.querySelector('[ref="cartItemCount"]')?.textContent;
    if (text) return parseInt(text, 10) || 0;
  }
  return 0;
}

/**
 * <powerlete-cart-undo> — cart "undo remove" controller (no visible UI of its own).
 *
 * Replicates the live Gecko behaviour: remove a line -> "{title} removed. Undo" ->
 * tapping Undo re-adds the variant.
 *
 * Why a layout-level controller + a separate in-drawer bar (powerlete-cart-undo-bar):
 * - The cart drawer is a modal <dialog> (top layer). A floating toast/popover renders
 *   BEHIND it, so the visible bar must live INSIDE the drawer to be seen.
 * - But the whole cart section is re-rendered by Horizon (morphSection, and sometimes a
 *   late async renderSection) on cart changes, which would wipe this controller's state
 *   AND reset the bar to hidden. So the controller is at layout level (persistent), and
 *   it re-asserts the in-drawer bar through every re-render via a MutationObserver while
 *   an undo is active. This is resilient to the multiple/async re-renders a single remove
 *   can trigger (the cart:update event alone is not — a late renderSection has no event).
 *
 * Flow:
 * 1. Capture-phase click on .cart-items__remove -> snapshot {variant, qty, title} from the
 *    data-pw-undo-* attributes (before Horizon removes the row); mark active; begin observing.
 * 2. The removal re-renders the cart section (re-creating the bar hidden). The observer
 *    fires and re-shows the bar; it keeps re-showing through any further re-render.
 * 3. NO auto-dismiss timer: the offer stays until Undo, explicit dismiss, the drawer
 *    closes, or a later cart ADD supersedes it (re-adding manually and then tapping a
 *    stale Undo would double-add, so any cart:add clears the offer). A later REMOVAL
 *    simply replaces the offer with the newer item's.
 * 4. Undo / dismiss are delegated (document-level) so they work on the re-created bar.
 *
 * Limitation: line-item custom properties (e.g. engraving) are not captured, so undo
 * restores variant + quantity only (matches the live theme).
 */
class PowerleteCartUndo extends HTMLElement {
  connectedCallback() {
    this.pending = null; // { variantId, quantity, title }
    this.active = false; // an undo is currently offered

    this.onRemoveClick = this.onRemoveClick.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onCartAdd = this.onCartAdd.bind(this);
    this.onDrawerClose = this.onDrawerClose.bind(this);
    this.reassert = this.reassert.bind(this);
    this.observer = new MutationObserver(this.reassert);

    // Capture phase: read the row data before Horizon removes the row.
    document.addEventListener('click', this.onRemoveClick, true);
    // Delegated (the bar is re-created on every cart re-render, so we can't bind to it directly).
    document.addEventListener('click', this.onDocClick);
    // A manual re-add makes the pending Undo stale (tapping it would double-add): clear the offer.
    document.addEventListener(CartAddEvent.eventName, this.onCartAdd);
    // Closing the drawer ends the session for the offer (capture 'close' — it doesn't bubble).
    document.addEventListener('close', this.onDrawerClose, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onRemoveClick, true);
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener(CartAddEvent.eventName, this.onCartAdd);
    document.removeEventListener('close', this.onDrawerClose, true);
    this.observer.disconnect();
  }

  /** The visible bar lives inside the drawer and is re-created on each cart re-render. */
  get bar() {
    return document.querySelector('[data-pw-undo-bar]');
  }

  /** @param {MouseEvent} event */
  onRemoveClick(event) {
    const target = event.target instanceof Element ? event.target.closest('.cart-items__remove') : null;
    if (!target) return;

    const variantId = target.getAttribute('data-pw-undo-variant');
    const quantity = parseInt(target.getAttribute('data-pw-undo-quantity') || '0', 10);
    const title = target.getAttribute('data-pw-undo-title') || '';
    if (!variantId || quantity <= 0) return;

    this.pending = { variantId, quantity, title };
    this.active = true;
    // No auto-dismiss: the offer persists until Undo, dismiss, drawer close, or a
    // superseding cart change. A further removal lands back here and replaces it.
    // Re-assert the bar through every DOM re-render (morph and late async renders) until hidden.
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.reassert();
  }

  /**
   * Any cart:update that ADDS from elsewhere (PDP form, quick-add, upsell) makes the
   * pending offer stale — tapping Undo after manually re-adding would double-add.
   * Line-quantity changes and removals ('cart-items-component') keep the offer, and our
   * own re-add ('powerlete-cart-undo') already hides via onUndo.
   * @param {Event & {detail?: {data?: {source?: string}}}} event
   */
  onCartAdd(event) {
    if (!this.active) return;
    const source = event.detail?.data?.source;
    if (source === 'cart-items-component' || source === 'powerlete-cart-undo') return;
    this.hide();
  }

  /** The drawer is a <dialog>; when it closes, the undo session is over. */
  onDrawerClose(event) {
    if (!this.active) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target && target.closest('cart-drawer-component, .cart-drawer__dialog')) this.hide();
  }

  /**
   * Show the bar (or, if already visible, just refresh its text). Only ONE offer is ever
   * shown — removing a second item while the bar is still up replaces the message with the
   * newer item rather than queueing, matching the live/Oner behaviour Matt confirmed.
   *
   * Guarded by data-pw-undo-title: this runs off a MutationObserver on document.body, and
   * writing the message is itself a mutation. Re-rendering on every call would re-trigger
   * the observer every time and loop forever; only touch the DOM when the title changed.
   */
  reassert() {
    if (!this.active) return;
    const bar = this.bar;
    if (!bar) return;
    const title = this.pending?.title || '';
    if (bar.dataset.pwUndoTitle !== title) {
      const template = bar.dataset.messageTemplate || '[title] was successfully removed from your cart.';
      const message = bar.querySelector('[data-pw-undo-message]');
      if (message) this.renderMessage(message, template, title);
      bar.dataset.pwUndoTitle = title;
    }
    if (!bar.hidden && bar.classList.contains('is-visible')) return;
    bar.hidden = false;
    void bar.offsetWidth; // reflow so the transition runs from the hidden state
    bar.classList.add('is-visible');
  }

  /**
   * Builds the message with the product title bolded (Oner-style), via DOM nodes rather
   * than innerHTML so the merchant-editable template can't inject markup.
   * @param {Element} el
   * @param {string} template - merchant string containing a literal `[title]` token.
   * @param {string} title
   */
  renderMessage(el, template, title) {
    const parts = template.split('[title]');
    el.replaceChildren();
    if (parts.length < 2) {
      el.textContent = template;
      return;
    }
    if (parts[0]) el.append(document.createTextNode(parts[0]));
    const strong = document.createElement('strong');
    strong.textContent = title;
    el.append(strong);
    if (parts[1]) el.append(document.createTextNode(parts[1]));
  }

  hide() {
    this.active = false;
    this.observer.disconnect();
    const bar = this.bar;
    if (bar) {
      bar.classList.remove('is-visible');
      const finalize = () => {
        bar.hidden = true;
        bar.removeEventListener('transitionend', finalize);
      };
      bar.addEventListener('transitionend', finalize);
      setTimeout(finalize, 400); // fallback if no transition fires
    }
    this.pending = null;
  }

  /** @param {MouseEvent} event */
  onDocClick(event) {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    if (el.closest('[data-pw-undo-dismiss]')) {
      event.preventDefault();
      this.hide();
      return;
    }
    if (el.closest('[data-pw-undo-button]')) {
      event.preventDefault();
      this.onUndo();
    }
  }

  async onUndo() {
    if (!this.pending) return;
    const { variantId, quantity } = this.pending;
    // Stop re-asserting before the re-add's re-render lands.
    this.active = false;
    this.observer.disconnect();
    this.pending = null;

    // this.dataset.sectionId is never set: powerlete-cart-undo (the controller, rendered
    // once at layout level) takes no section_id param, and none of the callers pass one
    // -- so `sections` was always empty here, the request never asked for section HTML,
    // and the header badge below had nothing to read a count from. Query the live
    // cart-items-component the same way powerlete-cart-upsell.js does.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const body = { items: [{ id: Number(variantId), quantity }] };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    try {
      // Theme.routes.cart_add_url already ends in `.js` (scripts.liquid appends it); the fallback
      // must include it too, and we must NOT append another `.js` here (would give `.js.js`).
      const res = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) return;
      const data = await res.json();
      // Let the drawer + free-ship bar refresh through the normal cart path. source is NOT
      // 'product-form-component', so cart-icon.js treats itemCount as the cart's new TOTAL
      // (it sets the badge), not a delta to add to the current count.
      document.dispatchEvent(
        new CartAddEvent(data, 'powerlete-cart-undo', {
          source: 'powerlete-cart-undo',
          itemCount: extractCartItemCount(data.sections, sectionIds),
          sections: data.sections,
        })
      );
    } catch (e) {
      // Network failure: leave the cart as-is.
    } finally {
      this.hide();
    }
  }
}

if (!customElements.get('powerlete-cart-undo')) {
  customElements.define('powerlete-cart-undo', PowerleteCartUndo);
}
